/* MENASA astrolabe backdrop: production build of the "Ex 2" look.
   Alter level, Light entrance, ivory ground, flat (no scroll 3D, no pointer tilt), clockwork rings.
   Everything the look does not use (other levels and reveals, glow filters, meteors, pulses, beams, the rule,
   3D separation, the tuning panel) is left out. The research playground with every option is ../index.html.

   Usage: <div class="mnast" data-mnast></div> inside a positioned section.
   Optional: data-piece-target=".selector" (sibling element the reveal brings in, e.g. the product gallery track),
             data-halo="0.55" (halo strength, 1 for the quarter-zip, 0.55 for the cap).
   API: window.MenasaAstrolabe.mount(el) returns an instance; mountAll() mounts every [data-mnast]. */
(() => {
  'use strict';

  /* ---------- the look ---------- */
  const CFG = {
    speed: .7,                       // master speed
    persp: 1400,                     // perspective for the burst's rush toward the viewer
    reveal: { speed: .9, power: .1, at: .2 }, // plays once 20% of the instrument is on screen
    light: {
      dark: .035, wait: .9, wave: 2.5, reach: 1.45, sparks: .35, zoom: .55, pieceAt: .25, pieceDur: 1.4,
      flares: .28, ringPower: .2, ringTime: .65, ringLine: .2, ringGrow: 57, ringSparks: .7, waveWidth: .2, waveOpacity: .18,
    },
    bounce: .5,                      // spring on each tick
    dust: { amount: .55, drift: .4 },
    // id: [start angle, degrees per second, tick interval (0 = smooth)]
    rot: { rete: [-20, 1.1, 0], ticks: [10, 3, .5], band: [15, -2.4, 2.5], sun: [0, .6, 0], moon: [40, .9, 0], star: [-40, .65, 0] },
  };
  const IGNITE = [289, 296, 302, 333, 349, 387, 395, 420, 450]; // radii that flare as the shockwave passes

  /* ---------- math ---------- */
  const D = Math.PI / 180, TAU = Math.PI * 2;
  const f = n => Math.round(n * 100) / 100;
  const P = (r, a) => [r * Math.sin(a * D), -r * Math.cos(a * D)];
  const pt = (r, a) => { const p = P(r, a); return f(p[0]) + ' ' + f(p[1]); };
  const xy = p => f(p[0]) + ' ' + f(p[1]);
  const add = (a, b, k = 1) => [a[0] + b[0] * k, a[1] + b[1] * k];
  const clamp = (v, a, b) => v < a ? a : v > b ? b : v;
  const lerp = (a, b, t) => a + (b - a) * t;
  const seg = (t, a, b) => clamp((t - a) / (b - a), 0, 1);
  const easeOut = x => 1 - Math.pow(1 - x, 3);
  const easeOut4 = x => 1 - Math.pow(1 - x, 4);
  const easeInOut = x => x < .5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
  const rgba = (rgb, a) => `rgba(${rgb},${clamp(a, 0, 1).toFixed(3)})`;
  const rng = seed => () => { seed |= 0; seed = seed + 0x6D2B79F5 | 0; let t = Math.imul(seed ^ seed >>> 15, 1 | seed); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; };
  const star8 = r => { let d = ''; for (let i = 0; i < 16; i++) d += (i ? 'L' : 'M') + pt(i % 2 ? r * .7654 : r, i * 22.5); return d + 'Z'; };
  const sparkle = s => `M0 ${-s}Q0 0 ${s} 0Q0 0 0 ${s}Q0 0 ${-s} 0Q0 0 0 ${-s}Z`;
  const circ = (r, cls) => `<circle r="${r}" class="${cls}"/>`;
  const RETE = 236 * 1.5 / 296; // Alter's rete rim is 354 units
  // A twinkling sparkle: an animated SVG path or, with a sky spec list, a spec the sky canvas draws
  // (animated SVG is restyled, laid out and repainted on the main thread every frame).
  const twinkle = (sky, rete, p, s, tt, td) => {
    if (!sky) return `<path transform="translate(${xy(p)})" d="${sparkle(s)}" class="fg tw" style="--tt:${tt}s;--td:-${td}s"/>`;
    (rete ? sky.rete : sky.tw).push({ x: p[0], y: p[1], s, tt: +tt, td: +td });
    return '';
  };
  // Segments with their running lengths, so the canvas can stroke any stretch of a line.
  const track = segs => { let len = 0; const s = segs.map(([p, q]) => [p, q, len, len += Math.hypot(q[0] - p[0], q[1] - p[1])]); return { segs: s, len }; };
  const disc = (x, y, r) => { const p = new Path2D(); p.arc(x, y, r, 0, TAU); return p; };
  // style writers that skip unchanged values
  const setT = (el, v) => { if (el._t !== v) { el._t = v; el.style.transform = v; } };
  const setO = (el, v) => { const s = v.toFixed(3); if (el._o !== s) { el._o = s; el.style.opacity = s; } };
  const setClip = (el, v) => { if (el._c !== v) { el._c = v; el.style.clipPath = v; } };
  // important: the silhouette must win over theme rules that strip filters on phones
  const setFilter = (el, v) => { if (el._f !== v) { el._f = v; el.style.setProperty('filter', v, 'important'); } };

  /* ---------- drawing: rete (spider-web star map, English star names) ---------- */
  const STARS = [
    ['Vega', 332, 238, 'out', -13, .16], ['Deneb', 24, 250, 'out', 13, -.16], ['Altair', 66, 230, 'out', 13, -.16],
    ['Arcturus', 294, 252, 'out', -13, .16], ['Regulus', 248, 148, 'eq', 14, .2], ['Spica', 214, 256, 'eq', -12, .18],
    ['Antares', 168, 262, 'eq', 12, -.18], ['Fomalhaut', 134, 266, 'eq', -12, .18], ['Sirius', 112, 148, 'eq', -14, -.2],
    ['Aldebaran', 6, 112, 'c', 28, -.25], ['Rigel', 300, 128, 'c', 26, .25], ['Procyon', 62, 122, 'c', -26, -.25],
  ];
  function blade(B, T, bend, w) {
    const dx = T[0] - B[0], dy = T[1] - B[1], L = Math.hypot(dx, dy);
    const u = [dx / L, dy / L], n = [-u[1], u[0]], b = bend * L;
    const c1 = add(add(B, u, L * .34), n, b), c2 = add(add(B, u, L * .76), n, b * .5);
    const d = `M${xy(add(B, n, w))}C${xy(add(c1, n, w * .75))} ${xy(add(c2, n, w * .3))} ${xy(T)}C${xy(add(c2, n, -w * .3))} ${xy(add(c1, n, -w * .75))} ${xy(add(B, n, -w))}Z`;
    const sg = bend > 0 ? -1 : 1, e = add(add(B, u, 10), n, sg * 17);
    return { d, e, curl: `M${xy(B)}Q${xy(add(add(B, u, -6), n, sg * 15))} ${xy(e)}` };
  }
  function buildRete(sky) {
    let s = circ(296, 's-thin') + circ(291, 's-hair') + circ(284, 's-hair soft');
    let beads = '';
    for (let a = 0; a < 360; a += 5) { const p = P(287.5, a); beads += `<circle cx="${f(p[0])}" cy="${f(p[1])}" r="${a % 15 ? .8 : 1.5}"/>`; }
    s += `<g class="fl">${beads}</g>`;
    const TH = [], RINGS = [92, 118, 146, 172];
    for (let a = 100; a <= 260; a += 16) TH.push(a);
    let web = '';
    TH.forEach(a => { web += `M${pt(68, a)}L${pt(186, a)}`; });
    RINGS.forEach((r, i) => { for (let k = 0; k < TH.length - 1; k++) web += `M${pt(r, TH[k])}Q${xy(P(r - 3 - i * 1.2, (TH[k] + TH[k + 1]) / 2))} ${pt(r, TH[k + 1])}`; });
    s += `<path d="${web}" class="s-hair soft"/>`;
    let dew = '';
    TH.forEach((a, i) => RINGS.forEach((r, j) => { if ((i + j) % 3 === 0) { const p = P(r, a); dew += `<circle cx="${f(p[0])}" cy="${f(p[1])}" r="1.3"/>`; } }));
    s += `<g class="fl o6">${dew}</g>`;
    s += `<g transform="translate(0 -83.5)">${circ(209.5, 's-thin')}${circ(196, 's-hair')}${circ(190, 's-hair soft')}`;
    let et = '', weave = '', signs = '';
    for (let a = 0; a < 360; a += 5) if (a % 30) et += `M${pt(209.5, a)}L${pt(204.5, a)}`;
    for (let a = 0; a < 360; a += 7.5) weave += `M${pt(204.5, a)}L${pt(196, a + 3.75)}L${pt(204.5, a + 7.5)}M${pt(196, a)}L${pt(200.2, a + 1.9)}`;
    for (let a = 0; a < 360; a += 30) signs += `<g transform="rotate(${a}) translate(0 -202.75)"><path d="M0 -6.5L4 0L0 6.5L-4 0Z" class="kn s-thin"/><circle r="1.3" class="fg"/></g>`;
    s += `<path d="${et}" class="s-hair"/><path d="${weave}" class="s-hair soft"/>${signs}</g>`;
    s += `<path d="M${pt(192, 95)}A192 192 0 0 1 ${pt(192, 265)}" class="s-thin"/>`;
    let sc = '';
    for (let a = 97; a < 263; a += 8) sc += `M${pt(186, a)}Q${pt(176, a + 4)} ${pt(186, Math.min(263, a + 8))}`;
    s += `<path d="${sc}" class="s-hair"/>`;
    s += '<path d="M-289 -2.2L-68 -2.2M68 -2.2L289 -2.2M-289 2.2L-68 2.2M68 2.2L289 2.2M-2.2 68L-2.2 289M2.2 68L2.2 289" class="s-hair"/>';
    [[-240, 0], [240, 0], [0, 192], [0, 240], [-192, 0], [192, 0]].forEach(([x, y]) => { s += `<path transform="translate(${x} ${y})" d="M0 -7L5 0L0 7L-5 0Z" class="kn s-thin"/><circle cx="${x}" cy="${y}" r="1.4" class="fg"/>`; });
    let blades = '', knobs = '', labels = '';
    STARS.forEach(([name, a, r, on, o, bend]) => {
      const baseR = on === 'out' ? 291 : on === 'eq' ? 192 : 66;
      const B = P(baseR, a + o), T = P(r, a), w = on === 'c' ? 3.8 : 5;
      const bl = blade(B, T, bend, w), bl2 = blade(B, T, -bend, w), rib = blade(B, T, bend, .01);
      blades += `<path d="${bl.curl}" class="s-thin"/><path d="${bl2.curl}" class="s-hair"/><path d="${bl.d}" class="bl s-thin"/><path d="${rib.d}" class="s-hair soft"/>`;
      knobs += `<circle cx="${f(bl.e[0])}" cy="${f(bl.e[1])}" r="2.4" class="kn s-thin"/><circle cx="${f(bl2.e[0])}" cy="${f(bl2.e[1])}" r="1.8" class="kn s-hair"/>`
        + `<circle cx="${f(B[0])}" cy="${f(B[1])}" r="4.4" class="kn s-thin"/><circle cx="${f(B[0])}" cy="${f(B[1])}" r="1.4" class="fl"/>`
        + twinkle(sky, true, T, 7.5, 4 + (a % 5), a % 7)
        + `<circle cx="${f(T[0])}" cy="${f(T[1])}" r="3.4" class="kn s-thin"/><circle cx="${f(T[0])}" cy="${f(T[1])}" r="1.6" class="fg"/>`;
      const lr = r < baseR ? r - 14 : r + 14, flip = a > 90 && a < 270;
      labels += `<text class="t-star" transform="rotate(${a}) translate(0 ${-lr})${flip ? ' rotate(180)' : ''}">${name}</text>`;
    });
    s += blades + knobs + labels;
    s += `<circle r="68" class="kn s-thin"/><circle r="63" class="s-hair soft"/><path d="${star8(60)}" class="s-thin"/><path d="${star8(60)}" transform="rotate(22.5)" class="s-hair"/>`;
    for (let k = 0; k < 16; k++) {
      const long = k % 2 === 0, tip = long ? -44 : -34, wd = long ? 7 : 5;
      s += `<path transform="rotate(${k * 22.5})" d="M0 -13C${wd} -20 ${wd} ${tip + 10} 0 ${tip}C-${wd} ${tip + 10} -${wd} -20 0 -13Z" class="kn s-hair"/>`;
    }
    s += `<circle r="13" class="kn s-thin"/><path d="${star8(10)}" class="s-hair"/><circle r="2.2" class="fg"/>`;
    return `<g transform="scale(${RETE.toFixed(4)})">${s}</g>`;
  }

  /* ---------- drawing: sparse sky (two constellations, scattered stars) ---------- */
  const CONS = [
    { c: [-440, -432], s: [[-95, -30], [-62, -18], [-35, -8], [-5, 0], [0, 32], [38, 40], [42, 5]], l: [[0, 1], [1, 2], [2, 3], [3, 6], [6, 5], [5, 4], [4, 3]], big: [0, 6] },
    { c: [445, -425], s: [[-30, -50], [30, -44], [-12, 2], [0, -2], [12, -6], [-26, 56], [34, 50], [0, -74]], l: [[0, 1], [0, 2], [1, 4], [2, 3], [3, 4], [2, 5], [4, 6], [7, 0], [7, 1]], big: [0, 6] },
    { c: [-450, 440] }, { c: [440, 440] }, // only keep scattered stars away from these corners
  ];
  function buildSky(sky) {
    const rnd = rng(11); // keep every rnd() call in order: it places the stars
    let cons = '', dots = '';
    CONS.slice(0, 2).forEach((c, ci) => {
      const lines = c.l.map(([a, b]) => `M${xy(add(c.c, c.s[a]))}L${xy(add(c.c, c.s[b]))}`).join('');
      if (sky) sky.lines.push({ i: ci, ...track(c.l.map(([a, b]) => [add(c.c, c.s[a]), add(c.c, c.s[b])])) });
      else cons += `<path d="${lines}" class="s-hair soft cons-line" pathLength="1" style="--i:${ci}"/>`;
      c.s.forEach((p, i) => {
        const q = add(c.c, p), big = c.big.includes(i);
        cons += `<circle cx="${f(q[0])}" cy="${f(q[1])}" r="${big ? 3.4 : 2.3}" class="fg"/>`;
        if (big) cons += twinkle(sky, false, q, 11, (3.5 + rnd() * 3).toFixed(1), (rnd() * 5).toFixed(1));
      });
    });
    for (let i = 0; i < 70; i++) {
      const a = rnd() * 360, r = 480 + rnd() * 270, p = P(r, a);
      if (Math.abs(p[0]) > 745 || Math.abs(p[1]) > 745 || CONS.some(c => Math.hypot(p[0] - c.c[0], p[1] - c.c[1]) < 105)) continue;
      dots += i % 4 === 0
        ? twinkle(sky, false, p, 3 + rnd() * 4, (3 + rnd() * 5).toFixed(1), (rnd() * 8).toFixed(1))
        : `<circle cx="${f(p[0])}" cy="${f(p[1])}" r="${(.8 + rnd() * 1.1).toFixed(2)}" class="fg" opacity="${(.3 + rnd() * .5).toFixed(2)}"/>`;
    }
    return dots + cons;
  }

  /* ---------- drawing: the Alter instrument (700-unit study scaled 1.5x onto the 1000-unit stage) ---------- */
  function buildAlter(uid, specs) {
    const r3 = n => Number(n.toFixed(3));
    const pt3 = (r, a, cx = 0, cy = 0) => { const t = (a - 90) * D; return [r3(cx + Math.cos(t) * r), r3(cy + Math.sin(t) * r)]; };
    const ci = (r, x = 0, y = 0, ex = '') => `<circle cx="${x}" cy="${y}" r="${r}" ${ex}/>`;
    const pa = (d, ex = '') => `<path d="${d}" ${ex}/>`;
    const li = (x1, y1, x2, y2, ex = '') => pa(`M${x1} ${y1}L${x2} ${y2}`, ex);
    const gr = (c, ex = '') => `<g ${ex}>${c}</g>`;
    const PAPER = 'var(--bg)';
    const starD = (x, y, r = 7, n = 4) => Array.from({ length: n * 2 }, (_, i) => `${i ? 'L' : 'M'}${pt3(i % 2 ? r * .24 : r, i * 180 / n, x, y).join(' ')}`).join('') + 'Z';
    const star = (x, y, r = 7, n = 4, ex = '') => pa(starD(x, y, r, n), ex);
    const ticks = (r, count, longEvery, x, y, major, minor) => {
      let d = '';
      for (let i = 0; i < count; i++) { const a = i * 360 / count; d += `M${pt3(r, a, x, y).join(' ')}L${pt3(r - (i % longEvery === 0 ? major : minor), a, x, y).join(' ')}`; }
      return pa(d);
    };
    const khatam = (x, y, r = 12, ex = '') => {
      const sq = rot => pa(Array.from({ length: 4 }, (_, i) => `${i ? 'L' : 'M'}${pt3(r, rot + 45 + i * 90).join(' ')}`).join('') + 'Z');
      return gr(sq(0) + sq(45) + star(0, 0, r * .61, 8, 'stroke-width=".65"') + ci(r * .18), `transform="translate(${x} ${y})" ${ex}`);
    };
    const woven = (radius, count = 48) => {
      let o = ''; const half = 180 / count;
      for (let i = 0; i < count; i++) {
        const a = i * 360 / count;
        o += pa(`M${pt3(radius, a - half).join(' ')}L${pt3(radius - 5, a).join(' ')}L${pt3(radius, a + half).join(' ')}L${pt3(radius + 5, a).join(' ')}Z`);
        if (i % 2 === 0) o += ci(1.1, ...pt3(radius, a), 'fill="currentColor" stroke="none"');
      }
      return gr(o, 'stroke-width=".62" opacity=".66"');
    };
    const cons = (pts, off, bright) => {
      const d = pts.map((p, i) => `${i ? 'L' : 'M'}${p.join(' ')}`).join('');
      let o = pa(d, 'stroke-width=".68" opacity=".43"');
      // the travelling dash and the breathing stars go to the sky canvas when it draws them
      if (specs) specs.traces.push({ off, ...track(pts.slice(1).map((p, i) => [pts[i], p])) });
      else o += pa(d, `data-trace="" pathLength="100" stroke-dasharray="8 92" stroke-width="1" opacity=".72" style="animation-delay:-${off}s"`);
      pts.forEach(([x, y], i) => {
        if (specs) specs.stars.push({ delay: off + i * 1.37, dot: disc(x, y, i === bright ? 2 : 1.4), star: new Path2D(starD(x, y, i === bright ? 7 : 3.4, 4)) });
        else o += gr(ci(i === bright ? 2 : 1.4, x, y, 'fill="currentColor" stroke="none"') + star(x, y, i === bright ? 7 : 3.4, 4, 'stroke-width=".55" fill="currentColor" fill-opacity=".18"'), `data-twinkle="" style="animation-delay:-${off + i * 1.37}s"`);
      });
      return o;
    };
    const sun = (x, y, r = 15) => {
      let rays = '';
      for (let i = 0; i < 24; i++) {
        const a = i * 15;
        rays += li(...pt3(r * .65, a), ...pt3(i % 2 ? r * 1.05 : r * 1.45, a), 'stroke-width=".7"');
        if (i % 3 === 0) rays += ci(.85, ...pt3(r * 1.63, a), 'fill="currentColor" stroke="none"');
      }
      // with the sky canvas on, the script turns the rays (data-rays) instead of a CSS animation
      return gr(ci(r * 1.75, 0, 0, `fill="${PAPER}" stroke="none"`) + gr(rays, specs ? 'data-rays=""' : 'data-sun-rays=""') + ci(r * .62) + ci(r * .43) + star(0, 0, r * .37, 8, 'stroke-width=".6"') + ci(1.4, 0, 0, 'fill="currentColor"'), `transform="translate(${x} ${y})"`);
    };
    const moon = (x, y, r = 17) => gr(ci(r + 7, 0, 0, `fill="${PAPER}" stroke="none"`) +
      pa(`M${r * .32} ${-r}A${r} ${r} 0 1 0 ${r * .76} ${r * .65}A${r * .88} ${r * .88} 0 0 1 ${r * .32} ${-r}Z`, 'fill="currentColor" fill-opacity=".27"') +
      pa(`M${r * .32} ${-r * .8}A${r * .82} ${r * .82} 0 0 0 ${r * .48} ${r * .54}`, 'opacity=".5" stroke-width=".5"') +
      star(r * .85, -r * .36, 4.5, 4, 'fill="currentColor" fill-opacity=".3"') + ci(1.1, r * 1.2, r * .25, 'fill="currentColor"') +
      ci(r + 5, 0, 0, 'stroke-dasharray="1 4" stroke-width=".6" opacity=".38"'), `transform="translate(${x} ${y})"`);
    const wrap = c => `<g class="alt" transform="scale(1.5)" fill="none" stroke="currentColor" stroke-width=".95" stroke-linecap="round" stroke-linejoin="round">${c}</g>`;

    // outer frame (fixed): double rim, degree ticks, numerals, khatams, and the four cardinal khatams with 0/90/180/270
    let frame = ci(292, 0, 0, 'stroke-width=".65"') + ci(288) + gr(ticks(287, 360, 5, 0, 0, 10, 3.5), 'opacity=".9" stroke-width=".6"');
    let labels = '';
    for (let a = 30; a < 360; a += 30) { if (a % 90 === 0) continue; const [x, y] = pt3(302, a); labels += `<text x="${x}" y="${y}" font-size="8">${a}</text>`; }
    frame += gr(labels, 'class="alt-engraving"');
    for (let a = 45; a < 360; a += 90) frame += khatam(...pt3(289, a), 5.5, `fill="${PAPER}" stroke-width=".7"`);
    for (const a of [0, 90, 180, 270]) {
      frame += gr(khatam(0, -340, a ? 9 : 10) + (a ? li(0, -331, 0, -326) : pa('M-4 -354L0 -371L4 -354M0 -335V-326')), `transform="rotate(${a})"`);
      frame += `<text class="alt-engraving" x="${pt3(315, a)[0]}" y="${pt3(315, a)[1]}" font-size="14.5" letter-spacing=".6">${a}</text>`;
    }
    const tickRing = ci(273, 0, 0, 'stroke-width=".6"') + ci(267) + gr(ticks(272, 72, 3, 0, 0, 5, 2), 'opacity=".6" stroke-width=".6"')
      + star(...pt3(270, 0), 4, 4, 'fill="currentColor" fill-opacity=".5"');
    const band = woven(259) + ci(251, 0, 0, 'stroke-width=".65"')
      + [0, 90, 180, 270].map(a => star(...pt3(255, a), 3.2, 4, 'fill="currentColor" fill-opacity=".45"')).join('');
    let sky = cons([[-172, -197], [-202, -218], [-228, -183], [-212, -149]], 1, 1)
      + cons([[189, -210], [222, -180], [238, -131], [215, -112]], 3, 1)
      + cons([[-259, -65], [-265, -7], [-251, 50], [-247, 100]], .5, 2)
      + cons([[257, -78], [270, -26], [255, 29], [248, 88]], 2.5, 0)
      + cons([[-110, 242], [-65, 260], [-18, 249], [39, 264], [96, 248]], 4, 3)
      + gr(khatam(-159, 196, 10) + khatam(164, 190, 9), 'opacity=".63"')
      + pa('M-116 -254Q0 -290 116 -254M-176 209Q0 300 176 209', 'stroke-width=".45" stroke-dasharray="1 5" opacity=".5"');
    let plate = [36, 66, 105, 152, 210, 276, 350].map(r => ci(r, 0, -78)).join('');
    for (let x = -680; x <= 680; x += 65) plate += pa(`M0 -268C${r3(x * .17)} -185 ${r3(x * .58)} -38 ${x} 288`);
    plate += li(-275, 0, 275, 0) + li(0, -275, 0, 275) + ci(6, 0, -78) + ci(11, 0, -78) + pa('M-270 75Q0 -153 270 75', 'stroke-width="1.5"') + ci(231);
    plate = `<defs><clipPath id="${uid}c">${ci(258)}</clipPath></defs>` + gr(plate, `clip-path="url(#${uid}c)" opacity=".23" stroke-width=".62"`);
    return {
      frame: wrap(frame), ticks: wrap(tickRing), band: wrap(band), sky: wrap(sky), plate: wrap(plate),
      sun: wrap(sun(0, -247)), moon: wrap(moon(0, -255)), star: wrap(khatam(0, -250, 11, `fill="${PAPER}"`)),
    };
  }

  /* ---------- component ---------- */
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  // The sky's moving details (sparkles, breathing stars, travelling dashes, the draw-in) are
  // drawn on the sky canvas, which already repaints every frame for the dust. Their shapes and
  // timings are worked out once here; each frame only draws. Reduced motion keeps the still SVG.
  const canvasSky = !reduce && typeof Path2D === 'function';
  const svg = (content, attrs = '', vb = '-500 -500 1000 1000') => `<svg viewBox="${vb}" aria-hidden="true" focusable="false" ${attrs}>${content}</svg>`;
  const rotSvg = (content, id) => svg(content, `class="mnast__rot" data-r="${id}"`);
  const ROT_IDS = Object.keys(CFG.rot);
  const instances = [];
  let uidN = 0, rafId = 0, last = 0, boost = 0, boostTarget = 0, lastY = scrollY, lastScrollT = 0;

  /* ---------- phones: the whole instrument on one canvas ----------
     Phones (and the light effects tier) draw the instrument on a single canvas. Its line art
     (sky and plate, rete, tick ring, woven band, frame, star, moon, sun) is rendered to images
     once, starting while the arrival curtain is up, and shared by both instruments; each frame
     only turns and stacks those images, at 30 fps outside the reveal. That replaces about a
     dozen large composited SVG layers, two masked sheens and a blended section overlay.
     Not on phones: the sheens, the section dusk, the sun rays' slow spin and the piece's glow. */
  const LITE = !reduce && typeof Path2D === 'function' && (() => {
    const q = window.MenasaQuality;
    return q ? !!(q.phone || q.tier === 'light') : matchMedia('(pointer: coarse)').matches && innerWidth < 1100;
  })();
  const art = { key: '', specs: null, src: null, layers: {}, run: 0, hurry: false, tok: null };
  const artSlot = () => new Promise(res => (art.hurry || !window.requestIdleCallback ? setTimeout(res, art.hurry ? 0 : 60) : requestIdleCallback(res, { timeout: 250 })));
  const artReady = () => !!(art.src && art.src.every(l => art.layers[l.id]));
  // The instrument's CSS with its tokens resolved, for SVG drawn as an image (an image cannot
  // see the page's styles or web fonts; the tiny engraved labels fall back to Georgia).
  const artCSS = (tok, px) => `.s-hair,.s-thin{fill:none;stroke:${tok.line}}.s-hair{stroke-width:${(.6 * px).toFixed(3)}}.s-thin{stroke-width:${(.95 * px).toFixed(3)}}`
    + `.soft{stroke:${tok.soft}}.kn{fill:${tok.bg}}.bl{fill:${tok.line};fill-opacity:.13}.fl{fill:${tok.line};stroke:none}.fg{fill:${tok.gold};stroke:none}.o6{opacity:.6}`
    + `.t-star{font:500 10.5px Georgia,'Times New Roman',serif;fill:${tok.line};text-anchor:middle;dominant-baseline:central;letter-spacing:.12em;text-transform:uppercase}`
    + `.alt{color:${tok.line}}.alt-engraving{fill:currentColor;stroke:none;font-family:Georgia,'Times New Roman',serif;font-weight:600;text-anchor:middle;dominant-baseline:central}`;
  function artSources() { // the same markup the SVG instrument uses, split into the canvas's layers
    const specs = { tw: [], rete: [], stars: [], traces: [], lines: [] }, A = buildAlter('mnastart', specs);
    const skyS = buildSky(specs), reteS = buildRete(specs);
    art.specs = specs;
    art.src = [ // E: half the drawn square, in instrument units; cy: its centre
      { id: 'base', E: 750, body: `<g opacity="${SKY_A}">${skyS}${A.sky}</g>${A.plate}` },
      { id: 'rete', E: 500, body: reteS },
      { id: 'ticks', E: 500, body: A.ticks },
      { id: 'band', E: 500, body: A.band },
      { id: 'frame', E: 600, body: A.frame },
      { id: 'star', E: 48, cy: -375, body: A.star },
      { id: 'moon', E: 48, cy: -382.5, body: A.moon },
      { id: 'sun', E: 48, cy: -370.5, body: A.sun },
    ];
  }
  function rasterSVG(svgText, size) {
    const url = URL.createObjectURL(new Blob([svgText], { type: 'image/svg+xml' })), img = new Image();
    // onload, then decode() where it works: Safari has rejected decode() for SVG images
    const loaded = new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
    img.src = url;
    return loaded.then(() => img.decode && img.decode().catch(() => {})).then(() => {
      const c = document.createElement('canvas');
      c.width = c.height = size;
      c.getContext('2d').drawImage(img, 0, 0, size, size);
      return c;
    }).finally(() => URL.revokeObjectURL(url));
  }
  // Renders every layer for this instrument width and resolution, one layer per idle slot.
  // Layers from an earlier size stay in use (scaled) until their replacement is ready.
  function wantArt(w, r, tok) {
    const key = Math.round(w) + '@' + r;
    if (art.key === key) return;
    art.key = key; art.tok = art.tok || tok;
    const run = ++art.run, u = w * r / 1000; // image pixels per instrument unit
    (async () => {
      if (!art.src) { await artSlot(); artSources(); instances.forEach(i => { if (i.lite) i.sky = art.specs; }); }
      for (const l of art.src) {
        await artSlot();
        if (run !== art.run) return;
        const size = Math.max(8, Math.round(2 * l.E * u)), cy = l.cy || 0;
        const doc = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${-l.E} ${cy - l.E} ${2 * l.E} ${2 * l.E}" width="${size}" height="${size}"><style>${artCSS(art.tok, 1000 / w)}</style>${l.body.replaceAll('var(--bg)', art.tok.bg)}</svg>`;
        try { const c = await rasterSVG(doc, size); if (run !== art.run) return; art.layers[l.id] = { c, E: l.E, cy }; }
        catch { /* a failed layer is simply not drawn */ }
      }
      art.hurry = false;
      performance.mark?.('menasa-astrolabe-art');
      wake();
    })();
  }

  // Mounting is cheap: the box already holds its square, and the piece turns into its silhouette
  // at once. The instrument itself (about 800 SVG nodes) is built in idle time after the page
  // loads, one per idle slot, or at once if it reaches the screen first, so building it never
  // competes with the hero's arrival.
  const idle = window.requestIdleCallback ? fn => requestIdleCallback(fn, { timeout: 1500 }) : fn => setTimeout(fn, 200);
  const waiting = [];
  function buildNext() { const inst = waiting.shift(); if (inst) { build(inst); if (waiting.length) idle(buildNext); } }
  function queue(inst) {
    waiting.push(inst);
    if (waiting.length > 1) return;
    if (document.readyState === 'complete') idle(buildNext); else addEventListener('load', () => idle(buildNext), { once: true });
  }
  // The landing page plays its reveals once per session (layout/theme.liquid); a later visit shows the instrument lit.
  const seenHome = el => document.documentElement.classList.contains('menasa-home-seen') && !!el.closest('.menasa-artistic');
  function mount(el) {
    if (el._mnast) return el._mnast;
    el.classList.add('mnast', 'is-pre');
    const inst = {
      el, n: instances.length, built: false, size: 0, visible: false, revealed: false, seen: seenHome(el),
      piece: el.dataset.pieceTarget ? el.parentElement.querySelector(el.dataset.pieceTarget) : null,
    };
    if (inst.piece && !reduce && !inst.seen) setFilter(inst.piece, 'brightness(0) saturate(0)'); // a silhouette until the blast lights it
    // Fine steps of visibility until the reveal has played, then only on/off screen.
    const TH = []; for (let i = 0; i <= 20; i++) TH.push(i / 20);
    const watch = thresholds => {
      const io = new IntersectionObserver(es => es.forEach(e => {
        inst.visible = e.isIntersecting;
        // SVG animations run on the main thread; hold them while the instrument is off screen
        el.classList.toggle('is-live', inst.visible);
        if (inst.visible && !inst.built) build(inst);
        if (inst.visible && inst.lite && !artReady()) art.hurry = true; // on screen before its art: finish now
        // reveal.at on screen, or as much as a short viewport can show (the entry's own boxes: no forced layout)
        const at = inst.seen ? 0 : Math.min(CFG.reveal.at, (e.rootBounds ? e.rootBounds.height : 800) / Math.max(1, e.boundingClientRect.height) * .9);
        if (inst.visible && !inst.revealed && e.intersectionRatio + .001 >= at) {
          inst.revealed = true; startReveal(inst);
          io.disconnect(); watch([0]);
        }
        if (inst.visible) wake();
      }), { threshold: thresholds });
      io.observe(el);
    };
    watch(TH);
    el._mnast = inst;
    instances.push(inst);
    if (LITE) build(inst); else queue(inst);
    return inst;
  }
  const tokens = el => {
    const cs = getComputedStyle(el), tk = n => cs.getPropertyValue(n).trim();
    return { dust: tk('--dust'), a: tk('--fx-a'), b: tk('--fx-b'), gold: tk('--gold'), line: tk('--line'), soft: tk('--line-soft'), bg: tk('--bg'), halo: tk('--halo'), py: parseFloat(tk('--py')) || .475 };
  };
  function buildLite(inst) {
    const el = inst.el;
    el.innerHTML = '<div class="mnast__big"><canvas></canvas></div>';
    const back = el.querySelector('canvas'), r = rng(3 + inst.n);
    Object.assign(inst, {
      lite: true, back, bctx: back.getContext('2d'), m: {}, dpr: 1, rv: null, stageS: 1, shown: false,
      parts: [], fx: { sparks: [], flares: [], igns: [] }, dustF: 1, tok: tokens(el), sky: art.specs, skyAt: null,
      haloA: parseFloat(el.dataset.halo) || 1, acc: 0, drawnAt: 0,
    });
    ROT_IDS.forEach(id => { const a = CFG.rot[id][0]; inst.m[id] = { ang: a, target: a, vel: 0, acc: 0 }; });
    for (let i = 0; i < 90; i++) inst.parts.push({ rho: .3 + r() * .44, th: r() * TAU, w: (.012 + r() * .035) * (r() < .85 ? 1 : -1), s: .5 + r() * 1.4, a: .25 + r() * .55, tw: .6 + r() * 1.8, ph: r() * 6.3, wob: r() * 6.3 });
    new ResizeObserver(() => {
      const w = el.clientWidth; if (!w) return;
      inst.size = w;
      inst.dpr = Math.min(devicePixelRatio || 1, window.MenasaQuality?.tier === 'light' ? 1.5 : 2);
      const cw = Math.round(w * 1.5 * inst.dpr);
      if (back.width !== cw) back.width = back.height = cw;
      wantArt(w, inst.dpr, inst.tok);
      if (inst.visible) wake();
    }).observe(el);
  }
  function build(inst) {
    if (inst.built) return;
    inst.built = true;
    if (LITE) { buildLite(inst); return; }
    const el = inst.el, sky = canvasSky ? { tw: [], rete: [], stars: [], traces: [], lines: [] } : null;
    const uid = 'mnast' + (++uidN), A = buildAlter(uid, sky);
    el.innerHTML = `<div class="mnast__stage">
      <div class="mnast__rv">
        <div class="mnast__big mnast__sky"><canvas></canvas>${svg(buildSky(sky) + A.sky, '', '-750 -750 1500 1500')}</div>
        <div class="mnast__lay">${svg(A.plate)}</div>
        <div class="mnast__lay">${rotSvg(buildRete(sky), 'rete')}</div>
        <div class="mnast__halo"></div>
        <div class="mnast__lay">${rotSvg(A.ticks, 'ticks')}</div>
        <div class="mnast__lay">${rotSvg(A.band, 'band')}<div class="mnast__sheen" data-s="band">${svg(A.band)}</div></div>
        <div class="mnast__lay">${svg(A.frame)}<div class="mnast__sheen" data-s="frame">${svg(A.frame)}</div></div>
        <div class="mnast__lay mnast__lights">${rotSvg(A.star, 'star')}${rotSvg(A.moon, 'moon')}${rotSvg(A.sun, 'sun')}</div>
      </div>
      <div class="mnast__big"><canvas></canvas></div>
    </div>`;
    const cvs = el.querySelectorAll('canvas');
    const host = el.closest('section') || el.parentElement;
    Object.assign(inst, {
      host, stage: el.querySelector('.mnast__stage'), rvEl: el.querySelector('.mnast__rv'),
      back: cvs[0], front: cvs[1], bctx: cvs[0].getContext('2d'), fctx: cvs[1].getContext('2d'),
      rot: {}, m: {}, sheen: {}, dpr: 1, rv: null, sheenA: 0, dusk: null,
      parts: [], fx: { sparks: [], flares: [], igns: [] }, frontDirty: false, dustF: 1, tok: {},
      sky, skyAt: null, rays: el.querySelector('[data-rays]'), raysA: null,
    });
    el.querySelectorAll('[data-r]').forEach(n => { inst.rot[n.dataset.r] = n; });
    el.querySelectorAll('[data-s]').forEach(n => { inst.sheen[n.dataset.s] = { h: n, s: n.firstElementChild }; });
    ROT_IDS.forEach(id => { const a = CFG.rot[id][0]; inst.m[id] = { ang: a, target: a, vel: 0, acc: 0 }; });
    if (el.dataset.halo) el.style.setProperty('--halo-a', el.dataset.halo);
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    inst.dusk = host.querySelector(':scope > .mnast-dusk'); // one dusk per section, shared by its instruments
    if (!inst.dusk) {
      inst.dusk = document.createElement('div'); inst.dusk.className = 'mnast-dusk'; inst.dusk.setAttribute('aria-hidden', 'true');
      host.appendChild(inst.dusk);
    }
    const cs = getComputedStyle(el), tk = n => cs.getPropertyValue(n).trim();
    inst.tok = { dust: tk('--dust'), a: tk('--fx-a'), b: tk('--fx-b'), gold: tk('--gold'), line: tk('--line'), soft: tk('--line-soft') };
    const r = rng(3 + inst.n);
    for (let i = 0; i < 90; i++) inst.parts.push({ rho: .3 + r() * .44, th: r() * TAU, w: (.012 + r() * .035) * (r() < .85 ? 1 : -1), s: .5 + r() * 1.4, a: .25 + r() * .55, tw: .6 + r() * 1.8, ph: r() * 6.3, wob: r() * 6.3 });

    new ResizeObserver(() => {
      const w = el.clientWidth; if (!w) return;
      inst.size = w;
      el.style.setProperty('--px', (1000 / w).toFixed(4));
      inst.dpr = Math.min(2, devicePixelRatio || 1);
      const cw = Math.round(w * 1.5 * inst.dpr);
      inst.back.width = inst.back.height = inst.front.width = inst.front.height = cw;
      if (reduce) drawDust(inst, 0, 0, 0);
      if (inst.visible) wake(); // built on reaching the screen: the loop waits for this size
    }).observe(el);
    applyRotations(inst);
  }

  /* ---------- Light entrance ---------- */
  function startReveal(inst) {
    if (reduce || inst.seen) { finishReveal(inst); return; }
    const L = CFG.light;
    inst.rv = { t0: performance.now(), fired: {}, W: 0, rt: 0, dur: Math.max(3.4, L.wait + L.wave + .6, L.pieceAt + Math.max(1.6, L.pieceDur + .3)) };
    if (inst.lite) { inst.shown = true; inst.el.classList.remove('is-pre'); return; }
    setClip(inst.rvEl, 'circle(0px at 50% 50%)');
    ['frame', 'band'].forEach(k => { inst.sheen[k].h.style.visibility = 'hidden'; });
    inst.el.classList.remove('is-pre');
  }
  function finishReveal(inst) {
    inst.rv = null;
    inst.el.classList.remove('is-pre');
    inst.el.classList.add('is-sky');
    if (inst.skyAt == null) inst.skyAt = performance.now() / 1000;
    if (inst.lite) {
      inst.shown = true; inst.stageS = 1; inst.dustF = 1;
      const p = inst.piece;
      if (p) { p.style.removeProperty('transition'); p.style.removeProperty('will-change'); setFilter(p, ''); }
      return;
    }
    setClip(inst.rvEl, ''); setT(inst.stage, 'none'); setO(inst.dusk, 0);
    ['frame', 'band'].forEach(k => { inst.sheen[k].h.style.visibility = ''; });
    if (inst.piece) setFilter(inst.piece, '');
    inst.dustF = 1;
  }
  function burst(inst, x, y, n, v0, v1) {
    for (let i = 0; i < n; i++) { const v = P(v0 + Math.random() * (v1 - v0), Math.random() * 360); inst.fx.sparks.push({ x, y, vx: v[0], vy: v[1], t: 0, dur: .5 + Math.random() * .9 }); }
  }
  function revealStep(inst, now) {
    const rv = inst.rv, L = CFG.light, pw = CFG.reveal.power, T = L.wait;
    const rt = (now - rv.t0) / 1000 * CFG.reveal.speed;
    rv.rt = rt;
    if (rt >= rv.dur) { finishReveal(inst); return; }
    const once = (k, fn) => { if (!rv.fired[k]) { rv.fired[k] = 1; fn(); } };
    const W = rt < T ? 0 : 800 * L.reach * easeOut4(seg(rt, T, T + L.wave));
    rv.W = W;
    // the whole instrument rushes toward the viewer and settles
    const dz = 80 * L.zoom * (1 - easeOut(seg(rt, T, T + L.wave + .05))), zoom = CFG.persp / (CFG.persp - dz);
    inst.dustF = seg(rt, T + .15, T + 2.05);
    if (inst.lite) {
      // the canvas draws the opening circle and the zoom; the piece is lit by one filter
      // transition, which the compositor runs without per-frame work on the main thread
      inst.stageS = zoom;
      if (inst.piece && rt >= T) once('lit', () => {
        const p = inst.piece;
        p.style.setProperty('will-change', 'filter');
        p.style.setProperty('transition', `filter ${(L.pieceDur / CFG.reveal.speed).toFixed(2)}s cubic-bezier(.65,0,.35,1)`, 'important');
        setFilter(p, 'brightness(1) saturate(1)');
      });
    } else {
      setClip(inst.rvEl, `circle(${(W * inst.size / 1000).toFixed(1)}px at 50% 50%)`);
      setT(inst.stage, `scale(${zoom.toFixed(4)})`);
      setO(inst.dusk, rt < T ? L.dark * easeOut(seg(rt, 0, T * .85)) : L.dark * (1 - easeOut(seg(rt, T, T + 1.35))));
    }
    // the piece: a silhouette the whole time, lit by the blast with a faint glow
    if (inst.piece && !inst.lite) {
      const lit = easeInOut(seg(rt, T, T + L.pieceDur)), g = Math.sin(Math.PI * seg(rt, T, T + 1.1));
      setFilter(inst.piece, lit < 1 || g > 0
        ? `brightness(${lit.toFixed(3)}) saturate(${lit.toFixed(3)}) drop-shadow(0 0 ${(7 * g).toFixed(1)}px ${rgba(inst.tok.a, .3 * g)})`
        : '');
    }
    if (rt >= T) once('burst', () => burst(inst, 0, 0, Math.round(90 * L.sparks * pw), 700, 1700));
    if (rt > T + .45) once('sky', () => { inst.el.classList.add('is-sky'); inst.skyAt = now / 1000; });
    IGNITE.forEach(r => { if (W >= r) once('ig' + r, () => {
      inst.fx.igns.push({ r, t: 0 });
      const n = Math.round(24 * L.ringSparks * pw);
      for (let i = 0; i < n; i++) { const a = Math.random() * 360, p = P(r, a), v = P(120 + Math.random() * 420, a + (Math.random() - .5) * 60); inst.fx.sparks.push({ x: p[0], y: p[1], vx: v[0], vy: v[1], t: 0, dur: .35 + Math.random() * .5 }); }
    }); });
    if (W >= 372) once('bodies', () => ['sun', 'moon', 'star'].forEach(id => {
      const p = P(372, inst.m[id].ang), s = 1.4 * L.flares * pw;
      inst.fx.flares.push({ x: p[0], y: p[1], t: 0, dur: .8, s });
      burst(inst, p[0], p[1], Math.round(10 * pw), 120, 500);
    }));
  }

  /* ---------- motion ---------- */
  function applyRotations(inst) { ROT_IDS.forEach(id => setT(inst.rot[id], `rotate(${inst.m[id].ang.toFixed(2)}deg)`)); }
  function step(inst, now, t, dt) {
    if (inst.rv) revealStep(inst, now);
    const ent = inst.rv ? Math.pow(1 - clamp((now - inst.rv.t0) / 2800, 0, 1), 3) : 0;
    const master = CFG.speed, mult = master * (1 + boost) + master * 10 * ent, stepMult = 1 + boost + 6 * ent;
    const zeta = 1 - .9 * CFG.bounce, k = 420, cd = 2 * zeta * Math.sqrt(k);
    ROT_IDS.forEach(id => {
      const m = inst.m[id], [, speed, tick] = CFG.rot[id];
      if (tick) {
        m.acc += dt;
        if (m.acc >= tick) { m.acc = m.acc - tick > tick ? 0 : m.acc - tick; m.target += speed * master * tick * stepMult; }
        for (let i = 0; i < 4; i++) { const h = dt / 4; m.vel += (k * (m.target - m.ang) - cd * m.vel) * h; m.ang += m.vel * h; }
      } else m.ang += speed * mult * dt;
    });
    if (inst.lite) { fxStep(inst, dt); drawLite(inst, t, dt, Math.max(.2, mult)); return; }
    applyRotations(inst);
    if (inst.rays) { // one turn per 116.4s, in whole degrees: sub-pixel steps, a few repaints a second
      const a = Math.round(-t * 360 / 116.4) % 360;
      if (inst.raysA !== a) { inst.raysA = a; inst.rays.setAttribute('transform', `rotate(${a})`); }
    }
    if (!inst.rv) {
      inst.sheenA += 20 * dt;
      const a1 = inst.sheenA, a2 = 140 - a1 * .8;
      setT(inst.sheen.frame.h, `rotate(${a1.toFixed(2)}deg)`); setT(inst.sheen.frame.s, `rotate(${(-a1).toFixed(2)}deg)`);
      setT(inst.sheen.band.h, `rotate(${a2.toFixed(2)}deg)`); setT(inst.sheen.band.s, `rotate(${(inst.m.band.ang - a2).toFixed(2)}deg)`);
    }
    fxStep(inst, dt);
    drawDust(inst, t, dt, Math.max(.2, mult));
    drawFront(inst);
  }

  /* ---------- canvases ---------- */
  // The sky's moving details, drawn from the specs built at mount with the timing of the CSS
  // keyframes they replace (mnast-trace, mnast-alt-trace, mnast-alt-star, mnast-tw).
  // The canvas shares the sky SVG's 1500-unit square: one unit is k canvas pixels.
  const SPARK = typeof Path2D === 'function' ? new Path2D('M0 -1Q0 0 1 0Q0 0 0 1Q0 0 -1 0Q0 0 0 -1Z') : null;
  const SKY_A = .46; // .mnast__sky svg opacity
  const inOut = x => x * x * (3 - 2 * x);
  function stretch(c, T, from, to) { // stroke the part of a track between two fractions of its length
    const a = from * T.len, b = to * T.len;
    c.beginPath();
    for (const [p, q, s0, s1] of T.segs) {
      if (s1 <= a || s0 >= b) continue;
      const L = s1 - s0 || 1, u = Math.max(0, (a - s0) / L), v = Math.min(1, (b - s0) / L);
      c.moveTo(p[0] + (q[0] - p[0]) * u, p[1] + (q[1] - p[1]) * u);
      c.lineTo(p[0] + (q[0] - p[0]) * v, p[1] + (q[1] - p[1]) * v);
    }
    c.stroke();
  }
  // The reveal's zoom scales everything about the canvas centre: s and o (s = 1 without it).
  const zoomOf = (inst, cx) => { const s = inst.lite ? inst.stageS : 1; return [s, cx * (1 - s)]; };
  function drawTwinkles(inst, t, list, rete) {
    const c = inst.bctx, W = inst.back.width, k = W / 1500, cx = W / 2, [zs, zo] = zoomOf(inst, cx);
    const r = rete ? inst.m.rete.ang * D : 0, cos = Math.cos(r), sin = Math.sin(r), R = rete ? RETE : 1;
    c.fillStyle = inst.tok.gold;
    for (const s of list) {
      const ph = ((t + s.td) / (2 * s.tt)) % 1, v = inOut(ph < .5 ? ph * 2 : 2 - ph * 2);
      const sc = (.55 + .45 * v) * s.s * R * k * zs, x = (s.x * cos - s.y * sin) * R, y = (s.x * sin + s.y * cos) * R;
      c.globalAlpha = (.2 + .8 * v) * (rete ? 1 : SKY_A);
      c.setTransform(sc * cos, sc * sin, -sc * sin, sc * cos, zs * (cx + x * k) + zo, zs * (cx + y * k) + zo);
      c.fill(SPARK);
    }
  }
  function drawSky(inst, t, withRete = true) {
    const S = inst.sky, c = inst.bctx, W = inst.back.width, k = W / 1500, cx = W / 2, [zs, zo] = zoomOf(inst, cx);
    // the two star-map constellations draw themselves in once the burst has passed
    c.setTransform(k * zs, 0, 0, k * zs, cx * zs + zo, cx * zs + zo);
    if (inst.skyAt != null) {
      c.strokeStyle = inst.tok.soft; c.lineWidth = .6 * inst.dpr / k; c.lineCap = 'butt';
      for (const L of S.lines) {
        const ph = (Math.max(0, t - inst.skyAt + L.i * 2.5) / 10) % 1;
        const shown = ph < .4 ? inOut(ph / .4) : 1, op = ph < .8 ? 1 : ph < .96 ? 1 - inOut((ph - .8) / .16) : 0;
        if (op > 0 && shown > 0) { c.globalAlpha = op * SKY_A; stretch(c, L, 0, shown); }
      }
    }
    drawTwinkles(inst, t, S.tw, false);
    // Alter's constellations (drawn at 1.5x): a dash travels each line, the stars breathe
    c.setTransform(k * 1.5 * zs, 0, 0, k * 1.5 * zs, cx * zs + zo, cx * zs + zo);
    c.strokeStyle = c.fillStyle = inst.tok.line; c.lineJoin = c.lineCap = 'round';
    c.lineWidth = 1; c.globalAlpha = .72 * SKY_A;
    for (const T of S.traces) {
      const s = ((t + T.off) / 16) % 1; // dash start, 8% long (pathLength 100, dasharray 8 92)
      stretch(c, T, s, Math.min(1, s + .08));
      if (s > .92) stretch(c, T, 0, s - .92);
    }
    c.lineWidth = .55;
    for (const s of S.stars) {
      const ph = ((t + s.delay) / 11.6) % 1;
      const op = ph < .48 ? .5 + .5 * inOut(ph / .48) : ph < .72 ? 1 - .3 * inOut((ph - .48) / .24) : .7 - .2 * inOut((ph - .72) / .28);
      c.globalAlpha = op * SKY_A; c.fill(s.dot); c.stroke(s.star);
      c.globalAlpha = op * SKY_A * .18; c.fill(s.star);
    }
    // the rete's sparkles turn with the rete
    if (withRete) drawTwinkles(inst, t, S.rete, true);
    c.setTransform(zs, 0, 0, zs, zo, zo); c.globalAlpha = 1; c.lineCap = 'butt'; c.lineJoin = 'miter'; // the dust's defaults
  }
  function drawDust(inst, t, dt, mult) {
    const c = inst.bctx, W = inst.back.width; if (!W) return;
    c.clearRect(0, 0, W, W);
    if (inst.sky) drawSky(inst, t);
    drawMotes(inst, t, dt, mult);
  }
  function drawMotes(inst, t, dt, mult) { // the drifting dust, in the current transform
    const c = inst.bctx, W = inst.back.width;
    if (inst.dustF <= 0) return;
    const k = W / 1500, cx = W / 2, dpr = inst.dpr, n = Math.round(CFG.dust.amount * (inst.size < 500 ? 55 : 90)), drift = CFG.dust.drift * mult;
    for (let i = 0; i < n; i++) {
      const p = inst.parts[i];
      p.th += p.w * drift * dt;
      const rr = (p.rho * 1000 + Math.sin(t * .4 + p.wob) * 8) * k, x = cx + rr * Math.cos(p.th), y = cx + rr * Math.sin(p.th);
      const al = p.a * inst.dustF * (.3 + .7 * (.5 + .5 * Math.sin(t * p.tw + p.ph))), s = p.s * dpr;
      c.fillStyle = rgba(inst.tok.dust, al);
      c.beginPath(); c.arc(x, y, s, 0, TAU); c.fill();
      if (p.s > 1.5) {
        const q = s * 3.2;
        c.strokeStyle = rgba(inst.tok.dust, al * .5); c.lineWidth = .6 * dpr;
        c.beginPath(); c.moveTo(x - q, y); c.lineTo(x + q, y); c.moveTo(x, y - q); c.lineTo(x, y + q); c.stroke();
      }
    }
  }
  function fxStep(inst, dt) {
    const F = inst.fx, L = CFG.light, damp = Math.exp(-dt * 3);
    for (let i = F.sparks.length - 1; i >= 0; i--) {
      const s = F.sparks[i];
      s.t += dt; s.vx *= damp; s.vy *= damp; s.vy += 90 * dt; s.x += s.vx * dt; s.y += s.vy * dt;
      if (s.t > s.dur) F.sparks.splice(i, 1);
    }
    for (let i = F.flares.length - 1; i >= 0; i--) if ((F.flares[i].t += dt) > F.flares[i].dur) F.flares.splice(i, 1);
    for (let i = F.igns.length - 1; i >= 0; i--) if ((F.igns[i].t += dt) > L.ringTime) F.igns.splice(i, 1);
  }
  function glowDot(c, tok, x, y, r, a) {
    const g = c.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, rgba(tok.b, a)); g.addColorStop(.3, rgba(tok.a, .6 * a)); g.addColorStop(1, rgba(tok.a, 0));
    c.fillStyle = g; c.beginPath(); c.arc(x, y, r, 0, TAU); c.fill();
  }
  function streak(c, tok, cx, cy, L, th, w, a) {
    if (L <= 0 || a <= 0) return;
    const dx = Math.cos(th) * L, dy = Math.sin(th) * L, g = c.createLinearGradient(cx - dx, cy - dy, cx + dx, cy + dy);
    g.addColorStop(0, rgba(tok.a, 0)); g.addColorStop(.5, rgba(tok.b, a)); g.addColorStop(1, rgba(tok.a, 0));
    c.strokeStyle = g; c.lineWidth = w; c.beginPath(); c.moveTo(cx - dx, cy - dy); c.lineTo(cx + dx, cy + dy); c.stroke();
  }
  const ring = (c, cx, R, w, style) => { c.strokeStyle = style; c.lineWidth = w; c.beginPath(); c.arc(cx, cx, Math.max(0, R), 0, TAU); c.stroke(); };
  const fxLive = inst => inst.rv || inst.fx.sparks.length || inst.fx.flares.length || inst.fx.igns.length;
  function drawFront(inst) {
    const c = inst.fctx, W = inst.front.width;
    if (!W) return;
    if (!fxLive(inst)) { if (inst.frontDirty) { c.clearRect(0, 0, W, W); inst.frontDirty = false; } return; }
    inst.frontDirty = true;
    c.clearRect(0, 0, W, W);
    drawEffects(inst, c, W);
  }
  function drawEffects(inst, c, W) { // the reveal's light, rings, sparks and flares, in the current transform
    const F = inst.fx, rv = inst.rv, tok = inst.tok, L = CFG.light;
    const k = W / 1500, cx = W / 2, dpr = inst.dpr;
    if (rv) {
      const rt = rv.rt;
      if (rt < 1.5) { // the point of light and its cross flare
        const a = rt < .35 ? easeOut(seg(rt, 0, .35)) : 1 - easeOut(seg(rt, .35, 1.5));
        glowDot(c, tok, cx, cx, (30 + 150 * easeOut(seg(rt, 0, .5))) * k, a);
        const len = 760 * k * easeOut(seg(rt, 0, .55));
        streak(c, tok, cx, cx, len, 0, 2.2 * dpr, a);
        streak(c, tok, cx, cx, len * .7, Math.PI / 2, 1.6 * dpr, a * .8);
        streak(c, tok, cx, cx, len * .35, Math.PI / 4, dpr, a * .5);
        streak(c, tok, cx, cx, len * .35, -Math.PI / 4, dpr, a * .5);
      }
      if (rt > .2 && rt < 2) { // slow god rays
        const al = Math.sin(Math.PI * seg(rt, .2, 2)) * .2, len = Math.max(rv.W, 220) * 1.15 * k;
        const g = c.createRadialGradient(cx, cx, 20 * k, cx, cx, len);
        g.addColorStop(0, rgba(tok.a, al)); g.addColorStop(1, rgba(tok.a, 0));
        c.fillStyle = g; c.beginPath();
        for (let i = 0; i < 16; i++) {
          const a = (i * 22.5 + rt * 14) * D, hw = (1.4 + (i % 3)) * D;
          c.moveTo(cx, cx); c.lineTo(cx + Math.cos(a - hw) * len, cx + Math.sin(a - hw) * len); c.lineTo(cx + Math.cos(a + hw) * len, cx + Math.sin(a + hw) * len); c.closePath();
        }
        c.fill();
      }
      if (rv.W > 0 && rt < 2.6) { // the shockwave ring
        const al = (1 - seg(rt, 1.7, 2.6)) * L.waveOpacity, R = rv.W * k, ww = L.waveWidth;
        ring(c, cx, R, 30 * ww * dpr, rgba(tok.a, .1 * al));
        ring(c, cx, R, 10 * ww * dpr, rgba(tok.a, .28 * al));
        ring(c, cx, R, 2.4 * ww * dpr, rgba(tok.b, .95 * al));
      }
    }
    F.igns.forEach(g => { // each ring flares and expands as the wave passes it
      const p = g.t / L.ringTime, al = Math.pow(1 - p, 1.5) * L.ringPower;
      ring(c, cx, (g.r + L.ringGrow * easeOut(p)) * k, Math.max(.6, 3.2 - 2 * p) * L.ringLine * dpr, rgba(tok.b, Math.min(1, .9 * al)));
    });
    c.lineCap = 'round';
    F.sparks.forEach(s => {
      const p = s.t / s.dur, x = cx + s.x * k, y = cx + s.y * k;
      c.strokeStyle = rgba(tok.b, 1 - p); c.lineWidth = Math.max(.3, 1.6 - p) * dpr;
      c.beginPath(); c.moveTo(x, y); c.lineTo(x - s.vx * .03 * k, y - s.vy * .03 * k); c.stroke();
    });
    F.flares.forEach(fl => {
      const p = fl.t / fl.dur, sc = Math.sin(Math.PI * p), x = cx + fl.x * k, y = cx + fl.y * k, s = 22 * k * fl.s * sc;
      glowDot(c, tok, x, y, s * 1.6 + .01, .7 * sc);
      c.save(); c.translate(x, y); c.rotate(p * 1.2); c.fillStyle = rgba(tok.b, sc);
      c.beginPath(); c.moveTo(0, -s); c.quadraticCurveTo(0, 0, s, 0); c.quadraticCurveTo(0, 0, 0, s); c.quadraticCurveTo(0, 0, -s, 0); c.quadraticCurveTo(0, 0, 0, -s); c.fill();
      c.restore();
    });
  }

  // Phones: the whole instrument, stacked from the art images in the SVG's own order.
  function drawLite(inst, t, dt, mult) {
    const c = inst.bctx, W = inst.back.width;
    if (!W) return;
    c.setTransform(1, 0, 0, 1, 0, 0); c.globalAlpha = 1;
    c.clearRect(0, 0, W, W);
    if (!inst.shown) return; // hidden until its reveal starts, as .is-pre hides it
    const k = W / 1500, cx = W / 2, [zs, zo] = zoomOf(inst, cx), rv = inst.rv, lay = art.layers;
    const put = (id, ang = 0, alpha = 1) => { // an art layer, turned about the instrument's centre
      const l = lay[id]; if (!l) return;
      c.setTransform(zs, 0, 0, zs, zo, zo); c.translate(cx, cx); if (ang) c.rotate(ang * D);
      c.globalAlpha = alpha;
      c.drawImage(l.c, -l.E * k, (l.cy - l.E) * k, 2 * l.E * k, 2 * l.E * k);
    };
    c.save();
    if (rv) { // the opening circle
      c.setTransform(zs, 0, 0, zs, zo, zo);
      c.beginPath(); c.arc(cx, cx, Math.max(0, rv.W * k), 0, TAU); c.clip();
    }
    if (inst.sky) drawSky(inst, t, false);
    drawMotes(inst, t, dt, mult);
    put('base');
    put('rete', inst.m.rete.ang);
    if (inst.sky) drawTwinkles(inst, t, inst.sky.rete, true);
    // the halo: radial-gradient(circle closest-side at 50% py, halo 34.56%, transparent 69.12%)
    const size = W / 1.5, left = cx - size / 2, hy = left + inst.tok.py * size;
    const R = Math.min(size / 2, inst.tok.py * size, (1 - inst.tok.py) * size);
    c.setTransform(zs, 0, 0, zs, zo, zo); c.globalAlpha = inst.haloA;
    if (!inst.haloG || inst.haloW !== W) {
      const g = c.createRadialGradient(cx, hy, 0, cx, hy, R);
      // fade to the halo's own colour at zero alpha (canvas gradients don't premultiply)
      const clear = /^rgba?\(/.test(inst.tok.halo) ? inst.tok.halo.replace(/^rgba?\(([^,]+),([^,]+),([^,)]+).*$/, 'rgba($1,$2,$3,0)') : 'rgba(255,255,255,0)';
      g.addColorStop(0, inst.tok.halo); g.addColorStop(.3456, inst.tok.halo); g.addColorStop(.6912, clear);
      inst.haloG = g; inst.haloW = W;
    }
    c.fillStyle = inst.haloG; c.fillRect(left, left, size, size);
    put('ticks', inst.m.ticks.ang);
    put('band', inst.m.band.ang);
    put('frame');
    put('star', inst.m.star.ang, .93); put('moon', inst.m.moon.ang, .93); put('sun', inst.m.sun.ang, .93);
    c.restore();
    if (fxLive(inst)) { c.setTransform(zs, 0, 0, zs, zo, zo); c.globalAlpha = 1; drawEffects(inst, c, W); }
  }

  /* ---------- loop: runs only while an instance is on screen ---------- */
  function frame(now) {
    const dt = Math.min(.05, (now - last) / 1000); last = now;
    boost += (boostTarget - boost) * .08; boostTarget *= .9;
    let any = false;
    for (let i = instances.length - 1; i >= 0; i--) if (!instances[i].el.isConnected) instances.splice(i, 1); // removed by the editor
    for (const inst of instances) if (inst.visible && inst.size) {
      any = true;
      if (!inst.lite) { step(inst, now, now / 1000, dt); continue; }
      // phones: 30 fps is plenty for these slow turns; the reveal and its sparks get every frame
      inst.acc += dt;
      if (!fxLive(inst) && now - inst.drawnAt < 30) continue;
      inst.drawnAt = now; step(inst, now, now / 1000, Math.min(.066, inst.acc)); inst.acc = 0;
    }
    rafId = any ? requestAnimationFrame(frame) : 0;
  }
  function wake() { if (!rafId && !reduce) { last = performance.now(); rafId = requestAnimationFrame(frame); } }
  addEventListener('scroll', () => { // scrolling past gives the rings a brief push
    const now = performance.now(), dt = Math.max(16, now - lastScrollT);
    boostTarget = Math.min(6, Math.abs(scrollY - lastY) / dt * 4);
    lastY = scrollY; lastScrollT = now;
  }, { passive: true });

  const mountAll = (root = document) => [...root.querySelectorAll('[data-mnast]')].map(mount);
  window.MenasaAstrolabe = { mount, mountAll };
  // re-mount when the theme editor re-renders a section
  document.addEventListener('shopify:section:load', e => mountAll(e.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mountAll());
  else mountAll();
})();
